import { useState, useEffect, useRef } from 'react';
import { Loader2, Lock, Eye, Gift, Calendar, X, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { updateInsideData } from '@/lib/fanProfile';
import { trackClick } from '@/lib/track';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITIES = ['Mumbai','Delhi','Bangalore','Hyderabad','Chennai','Kolkata','Pune','Ahmedabad','Jaipur','Lucknow','Chandigarh','Bhopal','Patna','Kochi','Guwahati'];
const INTERESTS = ['Films','Web-Series','Behind-the-Scenes','Events','Music','Fashion'];
const GENDERS = ['Male','Female','Non-binary'];

export default function InsideSection() {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', gender: '', city: '', cityOther: '', ageRange: '', phone: '', interests: [] });
  const [fomoCount, setFomoCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Live fan counter from /api/mail/stats — refreshes every 30s
  useEffect(() => {
    const fetchCount = () => {
      fetch(`${API}/mail/stats?adminKey=at-admin-2026`).then(r => r.json()).then(d => {
        setFomoCount(d.totalSubscribers || d.mongoSubscribers || 0);
      }).catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);
  const ref = useRef(null);

  // Reveal on scroll
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) e.target.classList.add('visible'); }, { threshold: 0.15 });
    el.querySelectorAll('.reveal-el').forEach(c => obs.observe(c));
    return () => obs.disconnect();
  }, []);

  const toggleInterest = (i) => setForm(f => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter(x => x !== i) : [...f.interests, i] }));

  const handleSubmit = async () => {
    setLoading(true);
    const city = form.city === 'Other' ? form.cityOther : form.city;
    const profileData = { name: form.name, email: form.email, gender: form.gender, city, ageRange: form.ageRange, phone: form.phone, interests: form.interests };
    updateInsideData(profileData);
    try {
      await axios.post(`${API}/subscribe`, { name: form.name, email: form.email, mobile: form.phone, city });
      trackClick({ film: 'inside', platform: 'website', action: 'profile_complete', section: 'inside' });
      // STEP 5: Sync to mailing system
      fetch(`${API}/mail/subscribe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profileData) }).catch(err => console.log('Mail sync deferred:', err));
    } catch {}
    setLoading(false);
    setDone(true);
    setTimeout(() => { setShowModal(false); setDone(false); }, 2000);
  };

  // Auto-detect city
  useEffect(() => {
    if (showModal && !form.city) {
      fetch(`${API}/detect-location`).then(r => r.json()).then(d => {
        if (d.city) setForm(f => ({ ...f, city: CITIES.includes(d.city) ? d.city : 'Other', cityOther: CITIES.includes(d.city) ? '' : d.city }));
      }).catch(() => {});
    }
  }, [showModal]);

  return (
    <section id="inside" data-testid="inside-section" ref={ref} className="relative py-24 md:py-40 lg:py-56 px-5 md:px-10 lg:px-20 bg-inside">
      {/* Animated rings */}
      <div className="absolute top-12 right-12 w-32 h-32 pointer-events-none hidden md:block">
        <div className="absolute inset-0 rounded-full border border-[var(--gold)]/10 animate-ring-pulse" />
        <div className="absolute inset-4 rounded-full border border-[var(--gold)]/15 animate-ring-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute inset-8 rounded-full border border-[var(--gold)]/20 animate-ring-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <p className="reveal-el font-['Cormorant_Garamond'] text-xs tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--gold)' }}>Inside</p>
      <h2 className="reveal-el font-['Playfair_Display'] italic text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight leading-[0.95]" style={{ color: 'var(--warm-cream)' }}>
        For those who <span className="font-semibold" style={{ color: 'var(--gold)' }}>stay</span>.
      </h2>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-16 md:mt-24">
        {[
          { icon: Eye, title: 'Unseen', desc: 'Things only found here.', href: '#unseen' },
          { icon: Calendar, title: 'Tonight', desc: 'Watch parties. Premieres.', href: '#tonight' },
          { icon: Gift, title: 'Gifts', desc: 'Rewards for being here.', href: '#gifts' },
        ].map((card) => (
          <a key={card.title} href={card.href} className="reveal-el p-6 md:p-8 rounded-sm transition-all duration-500 group hover:bg-white/[0.02]" style={{ border: '1px solid rgba(201,168,76,0.08)' }}>
            <card.icon className="w-5 h-5 mb-4" style={{ color: 'var(--gold)' }} strokeWidth={1.5} />
            <h3 className="font-['Playfair_Display'] italic text-xl font-normal" style={{ color: 'var(--warm-cream)' }}>{card.title}</h3>
            <p className="font-['Inter'] text-xs mt-2" style={{ color: 'var(--ash)' }}>{card.desc}</p>
          </a>
        ))}
      </div>

      {/* Enter CTA — FOMO + urgency */}
      <div className="reveal-el mt-14 md:mt-20 text-center">
        {/* FOMO counter */}
        <p className="font-['Inter'] text-sm mb-4" style={{ color: 'var(--ash)' }}>
          <span className="font-semibold" style={{ color: 'var(--gold)' }}>{fomoCount.toLocaleString()}</span> fans already inside
        </p>

        {/* Unlock benefits */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
          {['Unseen photos', 'Watch parties', 'Exclusive gifts'].map(item => (
            <span key={item} className="flex items-center gap-1.5 text-xs font-['Inter']" style={{ color: 'var(--ash)' }}>
              <Lock className="w-3 h-3" style={{ color: 'var(--gold)' }} strokeWidth={1.5} />{item}
            </span>
          ))}
        </div>

        {/* Main CTA — larger, gold, pulsing */}
        <button
          data-testid="enter-inside-btn"
          onClick={() => setShowModal(true)}
          className="font-['Cormorant_Garamond'] text-lg tracking-[0.2em] uppercase px-10 py-4 rounded-full transition-all duration-500 hover:shadow-[0_0_30px_rgba(201,168,76,0.2)]"
          style={{ background: 'var(--gold)', color: 'var(--deep-black)', fontWeight: 600 }}
        >
          Enter Inside
        </button>

        {/* WhatsApp alternative */}
        <div className="mt-6">
          <a
            href="https://chat.whatsapp.com/DmChZFC7pPS0HiOh4QG03b"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs font-['Inter'] transition-colors hover:text-[#25D366]"
            style={{ color: 'var(--ash)' }}
          >
            <svg viewBox="0 0 24 24" fill="#25D366" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
            Or join via WhatsApp
          </a>
        </div>

        {/* Urgency */}
        <p className="font-['Cormorant_Garamond'] italic text-xs mt-4" style={{ color: 'var(--ash)', opacity: 0.5 }}>
          Limited spots. Access closes when it closes.
        </p>
      </div>

      {/* Progressive Profiling Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center px-5" data-testid="inside-modal">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative z-10 w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowModal(false)} className="absolute -top-10 right-0 md:top-4 md:right-4 z-20" style={{ color: 'var(--ash)' }}><X className="w-5 h-5" /></button>

            {done ? (
              <div className="text-center py-16 rounded-sm" style={{ background: 'var(--charcoal)', border: '1px solid rgba(201,168,76,0.1)' }} data-testid="inside-success">
                <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4" style={{ border: '1px solid var(--gold)' }}>
                  <Check className="w-5 h-5" style={{ color: 'var(--gold)' }} />
                </div>
                <p className="font-['Playfair_Display'] italic text-2xl" style={{ color: 'var(--warm-cream)' }}>You're inside.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* LEFT: Quick Sign Up */}
                <div className="p-8 md:p-10 rounded-sm" style={{ background: 'var(--charcoal)', border: '1px solid rgba(201,168,76,0.1)' }}>
                {/* Step indicators */}
                <div className="flex gap-2 mb-8">
                  {[1,2,3].map(s => (
                    <div key={s} className="h-0.5 flex-1 rounded-full transition-all duration-300" style={{ background: step >= s ? 'var(--gold)' : 'rgba(255,255,255,0.08)' }} />
                  ))}
                </div>

                {/* Step 1: Welcome */}
                {step === 1 && (
                  <div data-testid="inside-step-1">
                    <h3 className="font-['Playfair_Display'] italic text-2xl mb-6" style={{ color: 'var(--warm-cream)' }}>Welcome</h3>
                    <div className="space-y-5">
                      <input data-testid="inside-name" type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="First name" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none transition-colors duration-300" style={{ borderColor: 'rgba(201,168,76,0.15)', color: 'var(--warm-cream)' }} />
                      <input data-testid="inside-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none transition-colors duration-300" style={{ borderColor: 'rgba(201,168,76,0.15)', color: 'var(--warm-cream)' }} />
                      <div>
                        <p className="text-xs mb-3" style={{ color: 'var(--ash)' }}>Gender</p>
                        <div className="flex gap-3">
                          {GENDERS.map(g => (
                            <button key={g} data-testid={`gender-${g.toLowerCase()}`} onClick={() => setForm(f => ({ ...f, gender: g }))} className={`gender-pill px-5 py-2 rounded-full text-xs font-['Inter'] tracking-wide ${form.gender === g ? 'selected' : ''}`}>{g}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => { if (form.name && form.email) setStep(2); }} className="w-full mt-8 py-3 rounded-full text-xs font-['Inter'] tracking-[0.15em] uppercase transition-all duration-300" style={{ background: form.name && form.email ? 'var(--gold)' : 'rgba(201,168,76,0.15)', color: form.name && form.email ? 'var(--deep-black)' : 'var(--ash)' }}>Continue</button>
                  </div>
                )}

                {/* Step 2: Where */}
                {step === 2 && (
                  <div data-testid="inside-step-2">
                    <h3 className="font-['Playfair_Display'] italic text-2xl mb-6" style={{ color: 'var(--warm-cream)' }}>Where are you?</h3>
                    <select data-testid="inside-city" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.15)', color: 'var(--warm-cream)' }}>
                      <option value="">Select city</option>
                      {CITIES.map(c => <option key={c} value={c} style={{ background: 'var(--charcoal)' }}>{c}</option>)}
                      <option value="Other" style={{ background: 'var(--charcoal)' }}>Other</option>
                    </select>
                    {form.city === 'Other' && (
                      <input type="text" value={form.cityOther} onChange={e => setForm(f => ({ ...f, cityOther: e.target.value }))} placeholder="Your city" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] mt-4 focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.15)', color: 'var(--warm-cream)' }} />
                    )}
                    <button onClick={() => form.city && setStep(3)} className="w-full mt-8 py-3 rounded-full text-xs font-['Inter'] tracking-[0.15em] uppercase transition-all duration-300" style={{ background: form.city ? 'var(--gold)' : 'rgba(201,168,76,0.15)', color: form.city ? 'var(--deep-black)' : 'var(--ash)' }}>Continue</button>
                  </div>
                )}

                {/* Step 3: Your world */}
                {step === 3 && (
                  <div data-testid="inside-step-3">
                    <h3 className="font-['Playfair_Display'] italic text-2xl mb-6" style={{ color: 'var(--warm-cream)' }}>Your world</h3>
                    <div className="space-y-5">
                      <div>
                        <p className="text-xs mb-3" style={{ color: 'var(--ash)' }}>Age range</p>
                        <div className="flex gap-2 flex-wrap">
                          {['18-24','25-34','35-44','45+'].map(a => (
                            <button key={a} onClick={() => setForm(f => ({ ...f, ageRange: a }))} className={`gender-pill px-4 py-2 rounded-full text-xs font-['Inter'] ${form.ageRange === a ? 'selected' : ''}`}>{a}</button>
                          ))}
                        </div>
                      </div>
                      <div className="relative">
                        <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone (optional)" className="w-full bg-transparent border-0 border-b px-0 py-3 text-sm font-['Inter'] focus:ring-0 focus:outline-none" style={{ borderColor: 'rgba(201,168,76,0.1)', color: 'var(--warm-cream)' }} />
                        <p className="flex items-center gap-1.5 mt-1.5 text-[10px]" style={{ color: 'var(--ash)', opacity: 0.5 }}>
                          <svg viewBox="0 0 24 24" fill="#25D366" className="w-3 h-3"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
                          For WhatsApp updates (optional)
                        </p>
                      </div>
                      <div>
                        <p className="text-xs mb-3" style={{ color: 'var(--ash)' }}>Interests</p>
                        <div className="flex gap-2 flex-wrap">
                          {INTERESTS.map(i => (
                            <button key={i} onClick={() => toggleInterest(i)} className={`interest-tag px-4 py-2 rounded-full text-xs font-['Inter'] ${form.interests.includes(i) ? 'selected' : ''}`}>{i}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button data-testid="inside-complete-btn" onClick={handleSubmit} disabled={loading} className="w-full mt-8 py-3 rounded-full text-xs font-['Inter'] tracking-[0.15em] uppercase transition-all duration-300 disabled:opacity-50" style={{ background: 'var(--gold)', color: 'var(--deep-black)' }}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "I'm in"}
                    </button>
                  </div>
                )}
                </div>

                {/* RIGHT: WhatsApp card */}
                <div className="p-8 md:p-10 rounded-sm flex flex-col items-center justify-center text-center" style={{ background: '#0a1a0a', border: '1px solid rgba(37,211,102,0.15)' }}>
                  <svg viewBox="0 0 24 24" fill="#25D366" className="w-12 h-12 mb-6"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.592-.836-6.313-2.228l-.44-.363-3.095 1.038 1.038-3.095-.363-.44A9.965 9.965 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
                  <h3 className="font-['Playfair_Display'] italic text-2xl mb-3" style={{ color: '#25D366' }}>Join via WhatsApp</h3>
                  <p className="font-['Inter'] text-xs mb-8" style={{ color: 'var(--ash)' }}>Instant access. No forms.</p>
                  <a
                    href="https://chat.whatsapp.com/DmChZFC7pPS0HiOh4QG03b"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-['Inter'] text-sm font-semibold tracking-wide px-8 py-3 rounded-full transition-all hover:bg-[rgba(37,211,102,0.15)]"
                    style={{ background: '#25D366', color: '#fff' }}
                  >
                    Open WhatsApp
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
